<template>
  <div>
    <l-map
      ref="DsioMap"
      :bounds="boundsValue"
      :options="mapOptions"
      @update:bounds="debouncedBounds"
    >
      <l-tile-layer :url="tileLayer.url" />
      <l-geo-json
        v-for="(layer, key) in layers"
        :key="key"
        :geojson="layer.geoJson"
        :options="layer.options"
        :optionsStyle="layer.style"
      />
      <l-control-attribution
        v-if="tileLayer.attribution && tileLayer.attribution !== ''"
        position="bottomleft"
        :prefix="tileLayer.attribution"
      />
      <v-marker-cluster
        v-if="conf.useClusters"
        :options="conf.markerClusterOptions"
      >
        <l-marker
          v-for="(item, key) in mapItems"
          :key="key"
          :lat-lng="item.marker"
          :icon="conf.usePriceMarkers || conf.useDivMarkers ? getDivIcon(item) : getIcon(item)"
          @click="() => { openDetail(key) }"
        />
      </v-marker-cluster>
      <template v-if="!conf.useClusters">
        <l-marker
          v-for="(item, key) in mapItems"
          :key="key"
          :lat-lng="item.marker"
          :icon="conf.usePriceMarkers || conf.useDivMarkers ? getDivIcon(item) : getIcon(item)"
          @click="() => { openDetail(key) }"
        />
      </template>
      <l-marker
        v-if="itemHighlighted && markerHighlited"
        :lat-lng="markerHighlited"
        :options="{zIndexOffset:1000}"
        :icon="conf.usePriceMarkers || conf.useDivMarkers ? getDivIconHover(mapItems[itemHighlighted]) : getIconHover(mapItems[itemHighlighted])"
      />
      <l-marker
        v-if="userPosition"
        :lat-lng="userPosition"
        :icon="iconUser"
        :options="{ interactive: false }"
      />

      <l-control-zoom
        v-if="!isMobileScreen"
        :options="Object.assign({},conf.zoomControlOption)"
      />

      <l-control
        v-if="conf.tileLayers && conf.tileLayers.length > 1"
        class="tileLayerControl"
        :options="Object.assign({}, conf.tileLayerControlOptions, isMobileScreen && conf.tileLayerControlOptionsSm ? conf.tileLayerControlOptionsSm : {})"
      >
        <div
          v-if="conf.tileLayers"
          class="tileLayers"
        >
          <button
            v-for="(tileLayerItem, tileLayerKey) in conf.tileLayers.filter(tileLayerItem => tileLayerItem.id !== currentTileLayer)"
            :key="tileLayerKey"
            class="tileLayer"
            :class="tileLayerItem.id"
            @click="currentTileLayer = tileLayerItem.id"
          >
            <span class="label">{{ tileLayerItem.name }}</span>
          </button>
        </div>
      </l-control>
    </l-map>
    <div
      :class="{'open': isSmallScreen && mapPopupContent }"
      class="map-popup-outside-wrapper"
    >
      <div
        class="map-popup-outside"
        v-html="mapPopupContent"
      />
      <button
        class="no-button close"
        @click="closeDetail"
      >
        <i class="dsio-close" />
      </button>
    </div>
  </div>
</template>

<script>
import baseEngines from './engines/baseEngines';
import { LControlAttribution, LGeoJson, LMap, LMarker, LTileLayer, LControl, LControlZoom } from 'vue2-leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
import toGeoJson from '@mapbox/togeojson';
import Axios from 'axios';
import debounce from 'lodash.debounce';
import 'leaflet-responsive-popup';
import 'leaflet.snogylop'; // inverser les layers
// import 'leaflet.fullscreen'; // plein ecran carte
import Events from 'classes/Events';

export default {
  components: {
    LMap,
    LTileLayer,
    LControlAttribution,
    LMarker,
    LGeoJson,
    LControl,
    LControlZoom,
    'v-marker-cluster': Vue2LeafletMarkerCluster,
  },
  mixins: [baseEngines],
  props: {
    mapItems: {
      type: Object,
      default: () => {},
    },
    bounds: {
      type: Array,
      default: () => [
        { lat: 0, lng: 0 },
        { lat: 0, lng: 0 },
      ],
    },
    conf: {
      type: Object,
      default: () => {},
    },
  },
  data () {
    return {
      mounted: false,
      mapPopupContent: null,
      icon: new L.Icon(this.conf.markerIcon),
      iconHover: new L.Icon(this.conf.markerIconHover),
      iconUser: new L.DivIcon({
        iconSize: [56, 56],
        iconAnchor: [28, 50],
        className: 'userIconWrapper',
        html: '<div class="userIcon"><i class="dsio-street-view-light"></i></div>',
      }),
      layers: [],
      currentTileLayer: this.getDefaultTileLayer(),
      tileLayer: this.getTileLayer(this.getDefaultTileLayer()),
      mapOptions: Object.assign({}, {
        scrollWheelZoom: false,
        zoomControl: false,
      }, this.conf.mapOptions),
    };
  },
  computed: {
    isSmallScreen () {
      return ['mobile', 'tablet'].indexOf(this.$root.breakpoint) > -1;
    },
    boundsValue: {
      get () {
        if (!this.filterData) {
          return this.conf.bounds;
        }

        const latlng = (this.filterData.length > 0) ? this.filterData.split('~') : [0, 0, 0, 0];
        return [
          { lat: latlng[0], lng: latlng[1] },
          { lat: latlng[2], lng: latlng[3] },
        ];
      },
      set (latlng) {
        const ne = Object.values(latlng[0]);
        const sw = Object.values(latlng[1]);

        // envoi evt avec bounds
        this.commit('UPDATE_SAVED_MAP_BOUNDS', [ne, sw]);

        this.filterData = [
          ne.join('~'),
          sw.join('~'),
        ].join('~');
      },
    },
    userPosition () {
      return this.$store.state[this.instanceId].userPosition || null;
    },
    itemHighlighted () {
      return this.$store.state[this.instanceId].itemHighlighted || false;
    },
    markerHighlited () {
      if (!this.mapItems[this.itemHighlighted]) { return false; }
      return this.mapItems[this.itemHighlighted].marker;
    },
  },
  watch: {
    mapItems (mapItems) {
      Events.mapChange(mapItems);
    },
    'app.state.viewMod' () {
      this.refreshMap();
    },
    currentTileLayer (tileLayerId) {
      this.tileLayer = this.getTileLayer(tileLayerId);
    },
  },
  created () {
    this.debouncedBounds = debounce(this.boundsUpdated, 500, { leading: true, trailing: false });

    // hack empêcher le scroll vers le conteneur carte
    // si clic sur les boutons zoom
    if (typeof L !== 'undefined') {
      L.Control.prototype._refocusOnMap = function _refocusOnMap () {};
    }

    if (this.isSmallScreen) {
      this.mapOptions.zoomControl = false;
    }

    if (window.lae.map.tileLayer) {
      this.conf.tileLayer = window.lae.map.tileLayer;
    }

    this.boundsValue = this.bounds;
  },
  mounted () {
    // Si des layers sont définits
    if (this.conf.layers.length > 0) {
      this.setLayers();
    }

    Events.mapLoaded(this.$refs.DsioMap.mapObject, { ...this.$props });
  },
  methods: {
    getTileLayer (tileLayerId) {
      // plan CDN en priorité si idTile === plan
      if (tileLayerId === 'plan' && window.lae.map.tileLayer) {
        return window.lae.map.tileLayer;
      }

      // retourne le tile layer
      const filteredTileLayer = this.conf.tileLayers.filter(tileLayer => tileLayer.id === tileLayerId);
      if (filteredTileLayer.length) {
        return filteredTileLayer[0];
      }

      if (window.lae.map.tileLayer) {
        return window.lae.map.tileLayer;
      } else if (this.conf.tileLayer) {
        return this.conf.tileLayer;
      }
    },
    getDefaultTileLayer () {
      if (this.conf.defaultTileLayer) {
        return this.conf.defaultTileLayer;
      }
      return 'plan';
    },
    /**
     * Rafraichis la carte
     **/
    refreshMap () {
      this.$refs.DsioMap.mapObject.invalidateSize();
    },
    /**
     * Retourne l'icone prix
     **/
    getDivIcon (item) {
      let className = item.className || '';
      if (this.conf.usePriceMarkers) {
        const price = item.price;
        if (!price) {
          className += ' ' + (this.conf.priceIconEmpty.className || '');
          return new L.DivIcon({
            ...this.conf.priceIconEmpty,
            className: className + '-wrapper price-icon-wrapper',
            html: '<div class="' + className + ' price-icon empty"></div>',
          });
        }

        className += ' ' + (this.conf.priceIcon.className || '');
        return new L.DivIcon({
          ...this.conf.priceIcon,
          className: className + '-wrapper price-icon-wrapper',
          html: '<div class="' + className + ' price-icon"><span class="price-label">' + price + '€</span><span class="price-anchor-point"></span></div>',
        });
      } else {
        const data = item.markerData;
        if (!data) {
          className += ' ' + (this.conf.divIconEmpty.className || '');
          return new L.DivIcon({
            ...this.conf.divIconEmpty,
            className: className + '-wrapper div-icon-wrapper',
            html: '<div class="' + className + ' div-icon empty"></div>',
          });
        }
        className += ' ' + (this.conf.divIcon.className || '');
        return new L.DivIcon({
          ...this.conf.divIcon,
          className: className + '-wrapper div-icon-wrapper',
          html: '<div class="' + className + ' div-icon"><span class="div-label">' + data + '</span><span class="div-anchor-point"></span></div>',
        });
      }
    },
    getDivIconHover (itemHighlighted) {
      let className = itemHighlighted.className || '';
      if (this.conf.usePriceMarkers) {
        const price = itemHighlighted && itemHighlighted.price ? itemHighlighted.price : null;

        if (!price) {
          className = ' ' + (this.conf.priceIconEmpty.className || '');
          return new L.DivIcon({
            ...this.conf.priceIconEmpty,
            className: className + '-wrapper price-icon-wrapper',
            html: '<div class="' + className + ' price-icon empty hover"></div>',
          });
        }

        className = ' ' + (this.conf.priceIcon.className || '');
        return new L.DivIcon({
          ...this.conf.priceIcon,
          className: className + '-wrapper price-icon-wrapper',
          html: '<div class="' + className + ' price-icon hover"><span class="price-label">' + price + '€</span><span class="price-anchor-point"></span></div>',
        });
      } else {
        const data = itemHighlighted.markerData;
        if (!data) {
          className = ' ' + (this.conf.divIconEmpty.className || '');
          return new L.DivIcon({
            ...this.conf.divIconEmpty,
            className: className + '-wrapper div-icon-wrapper',
            html: '<div class="' + className + ' div-icon empty hover"></div>',
          });
        }

        className = ' ' + (this.conf.divIcon.className || '');
        return new L.DivIcon({
          ...this.conf.divIcon,
          className: className + '-wrapper div-icon-wrapper',
          html: '<div class="' + className + ' div-icon hover"><span class="div-label">' + data + '</span><span class="div-anchor-point"></span></div>',
        });
      }
    },
    getIcon (item) {
      if (item.iconStyle) {
        return new L.Icon(this.conf['markerIcon-' + item.iconStyle]);
      }

      return this.icon;
    },
    getIconHover (itemHighlighted) {
      if (itemHighlighted.iconStyle) {
        return new L.Icon(this.conf['markerIconHover-' + itemHighlighted.iconStyle]);
      }

      return this.iconHover;
    },
    /**
     * Affiche les layers paramétrés dans conf
     */
    setLayers () {
      // loop layers
      for (let layer of this.conf.layers) {
        // get request
        Axios.get(layer.url, { responseType: 'text' })
          .then(({ data }) => {
            try {
              let geoJson = {};

              if (layer.type === 'geoJson') {
                geoJson = data;
              } else {
                const xml = (new DOMParser()).parseFromString(data, 'text/xml');

                // create geoJson string
                if (layer.type === 'kml') {
                  geoJson = toGeoJson.kml(xml);
                } else if (layer.type === 'gpx') {
                  geoJson = toGeoJson.gpx(xml);
                }
              }

              // Si geoJson n'est pas vide
              if (Object.entries(geoJson).length > 0) {
                this.layers.push({
                  geoJson: Object.freeze(geoJson),
                  options: layer.options || {},
                  style: layer.style,
                });
              }
            } catch (e) {
              console.error('Erreur layer: ' + layer.url);
              console.error(e);
            }
            return undefined;
          })
          .catch(console.error.bind(console));
      }
    },
    /**
     * ferme la popup detail
     **/
    closeDetail () {
      this.mapPopupContent = null;
      this.$emit('popupClose');
    },
    /**
     * Add marker popup
     * @param oiId
     */
    openDetail (oiId) {
      Axios.post(this.requestUrl + '/detail.map.ajx', null, { params: { ...this.app.params, oiId, lang: this.lang, responseType: 'html' } })
        .then(({ data }) => {
          if (data) {
            // popup fixed
            let popup = null;
            if (this.isSmallScreen) {
              this.mapPopupContent = data;
              this.$emit('popupOpen', { oiId });
            } else { // popup leaflet
              popup = L.responsivePopup(this.conf.markerPopupOptions)
                .setLatLng(this.mapItems[oiId].marker)
                .setContent(data)
                .openOn(this.$refs.DsioMap.mapObject);
              this.$emit('popupOpen', { oiId });
            }

            // mise à jour panier et ev click detail
            this.$nextTick(() => {
              // panier
              this.refreshBasket();

              Events.mapPopupOpen(this.$props);

              if (typeof jQuery !== 'undefined') {
                jQuery('.map-popup').click((ev) => {
                  let $target = jQuery(ev.target);
                  if ($target.is('a, button') && !$target.hasClass('dsio-detail-button')) {
                    return;
                  }
                  let prms = {};

                  // TODO supprimer dépendance à jQuery
                  // ne pas ouvrir si click sur lien
                  if ($target.is('a:not(".dsio-detail-button")') || $target.parents('a:not(.dsio-detail-button):first').length) {
                    return;
                  } else {
                    const $a = $target.is('a.dsio-detail-button') ? $target : $target.parents('a.dsio-detail-button:first');
                    if ($a.length) { // sur bouton
                      prms.hash = $a.attr('href').split('#')[1];
                    } else { // clic sur list-item
                      prms.hash = $target.parents('.list-item:first').find('a.dsio-detail-button:first').attr('href').split('#')[1];
                    }
                  }

                  let popupOpen = this.app.params.detailPopup;
                  let href = null;

                  // récupération du lien dans le dom
                  // el avec class dsio-detail-button
                  const $link = document.getElementsByClassName('list-item-' + oiId)[0].getElementsByClassName('dsio-detail-button')[0];

                  // vérification du lien
                  if (typeof $link !== 'undefined') {
                    href = $link.getAttribute('href');

                    // ouverture hors popup pour les liens http
                    if (href.indexOf('http') === 0) {
                      popupOpen = false;
                    }
                  }

                  // empecher le clic car gestion manuelle
                  ev.preventDefault();

                  if (popupOpen) {
                    this.commit('UPDATE_DETAIL', { oiId, prms });
                  } else if (href) {
                    $link.getAttribute('target') === '_blank' ? window.open(href) : window.location.href = href;
                  }

                  if (popup !== null) { // fermer la popup leaflet
                    popup.remove();
                  }
                });
              }
            });
          }
          return undefined;
        })
        .catch(console.error.bind(console));

      // this.$emit('detailOpen', { oiId });
    },
    boundsUpdated (latlng) {
      this.boundsValue = [
        latlng.getNorthEast(),
        latlng.getSouthWest(),
      ];
    },
  },
};
</script>
